import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlusIcon, SearchIcon, Trash2Icon, Loader2Icon, BookOpenIcon, TagIcon, ChevronRightIcon, MailIcon } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { emailTemplateLibraryApi, EmailLibraryRecord } from '../services/creatorApi';
import { toast } from 'sonner';
export function EmailTemplatesLibrary() {
  const navigate = useNavigate();
  const {
    currentWorkspace
  } = useWorkspace();
  const [templates, setTemplates] = useState<EmailLibraryRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  useEffect(() => {
    loadTemplates();
  }, [currentWorkspace.name]);
  const loadTemplates = async () => {
    setIsLoading(true);
    try {
      const all = await emailTemplateLibraryApi.list();
      const filtered = all.filter(t => !t.Team || t.Team === currentWorkspace.name);
      setTemplates(filtered);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to load email templates', {
        description: msg
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleDelete = async (id: string) => {
    setIsDeleting(true);
    try {
      await emailTemplateLibraryApi.delete(id);
      setTemplates(prev => prev.filter(t => t.ID !== id));
      toast.success('Email template deleted');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to delete template', {
        description: msg
      });
    } finally {
      setIsDeleting(false);
      setDeleteConfirm(null);
    }
  };
  const filtered = templates.filter(t => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (t.Template_Name || '').toLowerCase().includes(q) || (t.Description || '').toLowerCase().includes(q) || (t.Email_Subject || '').toLowerCase().includes(q) || (t.Category || '').toLowerCase().includes(q);
  });
  if (isLoading) {
    return <div className="flex items-center justify-center py-24" data-id="element-4112" data-mp="3i7mh1ikecedt">
        <Loader2Icon className="h-8 w-8 text-blue-500 animate-spin" data-id="element-4113" data-mp="3i72vzszg99vk" />
        <span className="ml-3 text-sm text-gray-500" data-id="element-4114" data-mp="3d91h7k923wpw">
          Loading email templates...
        </span>
      </div>;
  }
  return <div className="space-y-6" data-id="element-4115" data-mp="38fggvljzqqnj">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-4116" data-mp="33hyg4ig4nhqm">
        <div data-id="element-4117" data-mp="2zh4u5um8wd0d">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4118" data-mp="2zhnz8aqsr6wz">
            Email Templates Library
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-4119" data-mp="2ugqje4t9eoyn">
            Reusable email templates you can use when linking forms to
            confirmation emails.
          </p>
        </div>
        <button onClick={() => navigate('/email-templates-library/new')} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap flex-shrink-0" data-id="element-4120" data-mp="151ftdz33bs4s">
          <PlusIcon className="h-4 w-4" data-id="element-4121" data-mp="1ewzo4i5x4kry" />
          New Template
        </button>
      </div>

      {templates.length > 0 && <div className="flex items-center gap-4 bg-white border border-gray-200 rounded-lg p-4" data-id="element-4122" data-mp="maonx8m84lvo">
          <div className="relative flex-1 min-w-0" data-id="element-4123" data-mp="r8q6lsfqmj6m">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-4124" data-mp="r9hq44w28qgj" />
            <input type="text" placeholder="Search templates..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 pr-4 py-2 w-full border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500" data-id="element-4125" data-mp="v9jvao10820k" />
          </div>
          <span className="text-sm text-gray-500 flex-shrink-0" data-id="element-4126" data-mp="3kli4b6pydfrq">
            {filtered.length} template{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>}

      {filtered.length === 0 ? <div className="bg-white border border-gray-200 rounded-lg p-12 text-center" data-id="element-4127" data-mp="3ucwdk67phty2">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4" data-id="element-4128" data-mp="3pkl5gg2tb1uf">
            <BookOpenIcon className="h-8 w-8 text-gray-400" data-id="element-4129" data-mp="763rkp7u6a1n" />
          </div>
          <h3 className="text-sm font-semibold text-gray-900" data-id="element-4130" data-mp="28ajtrqvx7z9">
            {searchQuery ? 'No matching templates' : 'No email templates yet'}
          </h3>
          <p className="mt-1 text-sm text-gray-500 max-w-sm mx-auto" data-id="element-4131" data-mp="h4jhiov4nd9k">
            {searchQuery ? 'Try adjusting your search.' : 'Create reusable email templates for confirmation emails, welcome messages, and more.'}
          </p>
          {!searchQuery && <button onClick={() => navigate('/email-templates-library/new')} className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-4132" data-mp="2dz5wlyvhnb70">
              <PlusIcon className="h-4 w-4" data-id="element-4133" data-mp="jq0norxjg3f4" />
              Create First Template
            </button>}
        </div> : <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-4134" data-mp="onizqr6uqyrc">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-4135" data-mp="anzty51ijzi7">
            <thead className="bg-gray-50" data-id="element-4136" data-mp="eqedkrd0b3aj">
              <tr data-id="element-4137" data-mp="erfo8uq41nrq">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4138" data-mp="erz9akb24s9z">
                  Template
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4139" data-mp="5p6iei5seiko">
                  Subject
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4140" data-mp="1v333vlvq6qyr">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4141" data-mp="1ynlk1j2vzs5z">
                  Mode
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4142" data-mp="1ooe1mhhap7c0">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200" data-id="element-4143" data-mp="2hd1t4usn0sy0">
              {filtered.map(tpl => <tr key={tpl.ID} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/email-templates-library/${tpl.ID}`)} data-id="element-4144" data-mp="2cei8qytozp7r">
                  <td className="px-6 py-4" data-id="element-4145" data-mp="22oykfdn7n35i">
                    <div className="flex items-center gap-3" data-id="element-4146" data-mp="22o5e2ds5yfnw">
                      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center" data-id="element-4147" data-mp="3042utql572l9">
                        <MailIcon className="h-4 w-4 text-amber-600" data-id="element-4148" data-mp="2vcb1576owgqw" />
                      </div>
                      <div className="min-w-0" data-id="element-4149" data-mp="33n1t5sr01kol">
                        <p className="text-sm font-medium text-gray-900 truncate" data-id="element-4150" data-mp="37nbxz232251v">
                          {tpl.Template_Name || 'Untitled'}
                        </p>
                        {tpl.Description && <p className="text-xs text-gray-500 truncate max-w-xs" data-id="element-4151" data-mp="3hm1bkinx53cb">
                            {tpl.Description}
                          </p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-4152" data-mp="3qk3fp72y8c3t">
                    <span className="text-sm text-gray-600 truncate block max-w-[200px]" data-id="element-4153" data-mp="3vcmsi1ttb37k">
                      {tpl.Email_Subject || <span className="text-gray-400" data-id="element-4154" data-mp="45ttn4mhrg67">—</span>}
                    </span>
                  </td>
                  <td className="px-6 py-4" data-id="element-4155" data-mp="e4svo4cr4dk8">
                    {tpl.Category ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700" data-id="element-4156" data-mp="e2exhmmryl8g">
                        <TagIcon className="h-3 w-3" data-id="element-4157" data-mp="uflgm2mgul9f" />
                        {tpl.Category}
                      </span> : <span className="text-xs text-gray-400" data-id="element-4158" data-mp="18e1y8olv9w6c">—</span>}
                  </td>
                  <td className="px-6 py-4" data-id="element-4159" data-mp="frqoancdeiwv">
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${tpl.Builder_Mode === 'builder' ? 'bg-purple-50 text-purple-700' : 'bg-gray-100 text-gray-600'}`} data-id="element-4160" data-mp="fsjxrw2prmip">
                      {tpl.Builder_Mode === 'builder' ? 'Builder' : 'Code'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-4161" data-mp="3t6diovpwhnem">
                    <div className="flex items-center justify-end gap-2" data-id="element-4162" data-mp="3t52c6o52m1jt">
                      <button onClick={e => {
                  e.stopPropagation();
                  setDeleteConfirm(tpl.ID);
                }} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete" data-id="element-4163" data-mp="3o6itcwlrx5vo">
                        <Trash2Icon className="h-4 w-4" data-id="element-4164" data-mp="1znxtoxev170v" />
                      </button>
                      <ChevronRightIcon className="h-4 w-4 text-gray-300" data-id="element-4165" data-mp="1zljymok6jutb" />
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>}

      {deleteConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-4166" data-mp="2dg4benxnvm2f">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-4167" data-mp="2iefhqooqzefw">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setDeleteConfirm(null)} data-id="element-4168" data-mp="2igihw1jm6nyc" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-id="element-4169" data-mp="16ziqr9906f5w">
              <div className="flex items-center gap-4" data-id="element-4170" data-mp="33oyqw7alccll">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-4171" data-mp="33rl9d1z0scil">
                  <Trash2Icon className="h-6 w-6 text-red-600" data-id="element-4172" data-mp="38qeeo745kx7o" />
                </div>
                <div data-id="element-4173" data-mp="2txkuw9hulux7">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-4174" data-mp="2yys0q30fkk18">
                    Delete Template
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-4175" data-mp="2kyr0mprzpgmt">
                    Are you sure? This cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6" data-id="element-4176" data-mp="2b12yhes216um">
                <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-4177" data-mp="2b2ximqvi4uxn">
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteConfirm)} disabled={isDeleting} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50" data-id="element-4178" data-mp="e813f0gdt9gs">
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}